import React from 'react';
import { motion } from 'framer-motion';
import AIVideoHeader from '../AIVideoHeader';
import AIVideoFooter from '../AIVideoFooter';
import VideoBackground from '../VideoBackground';

interface Step7AIFamiliarityProps {
  onNext: (answer: string) => void;
  onBack: () => void;
}

const Step7AIFamiliarity: React.FC<Step7AIFamiliarityProps> = ({ onNext, onBack }) => {
  const options = [
    { id: 'expert', label: '🤖 Expert (Regularly use AI in my creative workflow)', emoji: '🤖' },
    { id: 'aware', label: '💡 Aware (Heard about it, keen to learn practical uses)', emoji: '💡' },
    { id: 'curious', label: '❓ Curious (New to the concept of AI for creativity)', emoji: '❓' },
    { id: 'basic', label: '🧑‍💻 Basic (Used general AI tools, not for video)', emoji: '🧑‍💻' },
  ];

  const fallbackBackground = (
    <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black">
      <motion.div
        className="absolute inset-0 opacity-20"
        animate={{
          background: [
            "radial-gradient(circle at 20% 50%, #ffffff 1px, transparent 1px)",
            "radial-gradient(circle at 80% 50%, #ffffff 1px, transparent 1px)",
            "radial-gradient(circle at 40% 50%, #ffffff 1px, transparent 1px)"
          ]
        }}
        transition={{ duration: 10, repeat: Infinity }}
      />
    </div>
  );

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col">
      <VideoBackground
        videoSrc="https://res.cloudinary.com/drinkamrs/video/upload/v1752753031/Characters_wole_ai_02_a_202507170450_g9mto0.mp4"
        fallbackComponent={fallbackBackground}
      />
      <AIVideoHeader 
        currentStep={7} 
        totalSteps={8} 
        onBack={onBack} 
      />

      <div className="relative z-10 flex-1 px-4 py-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-sm mx-auto"
        >
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-8 drop-shadow-lg font-playfair" style={{ textShadow: "0 2px 4px rgba(0,0,0,0.8)" }}>
            How familiar are you with using <span className="text-hollywood-gold">AI</span> for creative tasks?
          </h2>

          <div className="space-y-2.5">
            {options.map((option, index) => (
              <motion.button
                key={option.id}
                onClick={() => onNext(option.id)}
                className="w-full p-3 text-left bg-black/30 backdrop-blur-md hover:bg-hollywood-gold/20 border border-hollywood-gold/30 hover:border-hollywood-gold-light/70 rounded-xl transition-all duration-200 group shadow-lg"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{option.emoji}</span>
                   <span className="font-bold text-white group-hover:text-hollywood-shimmer text-base">
                    {option.label.replace(option.emoji + ' ', '')}
                   </span>
                </div>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </div>

      <AIVideoFooter />
    </div>
  );
};

export default Step7AIFamiliarity;